import React, {useEffect} from 'react';
import classNames from 'classnames';
import {useDispatch, useSelector} from 'react-redux';
import Card from 'src/components/Cards/Card';
import WidgetCard from 'src/components/widget-card';
import {EducationPhase} from 'src/types/enums';
import ChecklistGroup from 'src/components/checklist/checklist-group';
import {ChecklistData} from '../../hooks/useChecklist';
import styles from './styles.module.scss';
import {getInitialUserInfo} from '../../../shared/portfolio/actions/portfolio';
import {Store} from '../../types/store';
import HomepageRecommendationBucket from './HomepageRecommendationBucket';
import PlatformService from '../../services/PlatformService';

interface HomePageProps {
  checklistData: ChecklistData;
  educationPhase: EducationPhase;
}

const HomePage = ({checklistData, educationPhase}: HomePageProps) => {
  const student = useSelector((state: Store) => state.student);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!student.firstName) {
      dispatch(getInitialUserInfo());
    }
  }, [dispatch, student]);

  const invitationLink = PlatformService.isNativeApp()
    ? '/invitations/contacts'
    : '/home/invite/create';

  const WIDGETS = [
    ...(educationPhase === EducationPhase.HIGH_SCHOOL
      ? [
          {
            title: 'Scholarships',
            subtitle: 'Discover Micro-Scholarships',
            link: '/micro-scholarships',
            icon: 'dollar-sign',
          },
        ]
      : []),
    {
      title: 'Portfolio',
      subtitle: 'Build your college resume',
      link: '/portfolio',
      icon: 'file-alt',
    },
    {
      title: 'Checklist',
      subtitle: 'Complete your college checklist',
      link: '/checklist',
      icon: 'check',
    },
    {title: 'Colleges', subtitle: 'Discover colleges', link: '/colleges', icon: 'university'},
    {title: 'Videos', subtitle: 'Discover videos', link: '/videos', icon: 'video'},
    {
      title: 'Invite',
      subtitle: 'Refer a friend, and you’ll both get a $100 micro-scholarship',
      link: {pathname: invitationLink, state: {skipScrolling: true}},
      icon: 'heart',
    },
  ];

  let currentGroup = checklistData.checklist?.groups.find((group) => group.current);
  currentGroup = currentGroup || checklistData.checklist?.groups[0];

  return (
    <>
      {!student.firstName ? (
        <div>
          <h1 className={classNames('shared-shimmer-text large', styles.shimmerHeader)}></h1>
          <div className="shared-shimmer-text small"></div>
        </div>
      ) : (
        <div>
          <h1 className="heading-1">
            Welcome, {student.firstName} {student.lastName}
          </h1>
          <div className="shared-font body">Discover how to earn more money for college.</div>
        </div>
      )}
      <h2 className={styles.section}>Your Checklist</h2>
      <p className={styles.sectionSubtitle}>
        Items on the checklist will be automatically checked off for you once completed. Some items
        will display potential earnings for completing that action. Not every college will award for
        every action. Navigate to a college&apos;s page to learn more about what they award for.
      </p>
      <Card>
        {currentGroup ? (
          <ChecklistGroup title={currentGroup.name} list={currentGroup.items} />
        ) : (
          <div className={classNames('shared-shimmer', styles.shimmerListItem)}></div>
        )}
      </Card>
      <HomepageRecommendationBucket />
      <h2 className={styles.section}>Explore Raise Me</h2>
      <div className={styles.widgets}>
        {WIDGETS.map((widget) => (
          <WidgetCard key={widget.title} {...widget} />
        ))}
      </div>
    </>
  );
};

export default HomePage;
